import React, { useState, KeyboardEvent, useEffect, useLayoutEffect, useRef, PointerEvent as ReactPointerEvent, DragEvent as ReactDragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { useVoiceNoteRecorder, RecordedVoiceNote } from '../../hooks/useVoiceNoteRecorder';
import { formatVoiceNoteDuration, MAX_VOICE_NOTE_MS } from '../../helpers/voiceNoteHelper';
import { useIsMobile } from '../../hooks/useIsMobile';
import { resizeTextareaToContent } from '../../helpers/textareaAutoresize';
import { MicrophoneIcon, SendIcon } from './icons/ChatIcons';
import { isSpreadsheetFile, setHandoffFile } from '../../services/chat/chatImportHandoff';
import { useChat } from '../../contexts/ChatContext';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  autoSpeakEnabled?: boolean;
  onToggleAutoSpeak?: () => void;
  onCancelSpeech?: () => void;
  /** Micrófono deshabilitado (KAN-218): la Web Speech API no transcribe offline. */
  micDisabled?: boolean;
  micDisabledHint?: string;
  /**
   * Sin conexión (KAN-225): el micrófono graba una nota de voz en vez de
   * dictar con la Web Speech API. La nota se transcribe al reconectar.
   */
  voiceNoteMode?: boolean;
  onVoiceNote?: (note: RecordedVoiceNote) => void;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  disabled = false,
  placeholder = 'Escribí tu mensaje...',
  autoSpeakEnabled = false,
  onToggleAutoSpeak,
  onCancelSpeech,
  micDisabled = false,
  micDisabledHint = 'El dictado necesita conexión',
  voiceNoteMode = false,
  onVoiceNote,
}) => {
  const [input, setInput] = useState('');
  const navigate = useNavigate();
  const { getActiveSessionId } = useChat();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Feedback del adjunto: planilla → derivada al importador; otro archivo → aviso.
  const [fileNotice, setFileNotice] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Espejo del input para los handlers de teclado (closure siempre fresco).
  const inputRef = useRef(input);
  inputRef.current = input;
  const { isMobile } = useIsMobile();
  const {
    isListening,
    transcript,
    interimTranscript,
    isSupported: isSpeechSupported,
    error: speechError,
    startListening,
    stopListening,
    resetTranscript,
  } = useSpeechRecognition({
    lang: 'es-ES',
    // DESKTOP: continuous: true para que una misma pulsación capture todo lo dicho
    // a través de pausas cortas (acá sí funciona).
    // MOBILE (Android Chrome): continuous: false. La implementación de la Web Speech
    // API en Android NO soporta de verdad `continuous`: produce una lista de resultados
    // que crece y se re-emite, duplicando el texto ("anota anota anota…"). Con false,
    // cada pulsación captura una frase limpia; para seguir, soltá y volvé a presionar
    // (se appendea sobre el texto existente). Refs: react-speech-recognition #18,
    // Andrea Giammarchi "Taming the Web Speech API", chromium #40324711.
    continuous: !isMobile,
    interimResults: true,
  });

  // Notas de voz offline (KAN-225): mismo gesto push-to-talk, otro motor.
  const recorder = useVoiceNoteRecorder({ onComplete: (note) => onVoiceNote?.(note) });
  const recording = voiceNoteMode ? recorder.isRecording : isListening;
  const micSupported = voiceNoteMode ? recorder.isSupported : isSpeechSupported;

  // Actualizar el input con la transcripción
  useEffect(() => {
    if (transcript) {
      setInput(transcript);
    }
  }, [transcript]);

  // Edición manual del input: el usuario toma el control del texto. Si el
  // dictado estaba activo se corta (reconciliar un borrado contra los
  // event.results cumulativos del navegador es frágil), y SIEMPRE se resetea
  // el transcript del hook — así ningún resultado tardío de una escucha ya
  // frenada puede volver a llenar el input con lo dictado ("se autocompletaba
  // con todo lo que se había hablado"). Para seguir dictando, se aprieta el
  // mic de nuevo: startListening(input) rebasa sobre el texto editado.
  const handleInputChange = (value: string) => {
    setInput(value);
    if (isListening) stopListening();
    resetTranscript();
  };

  // KAN-183: el resize es reactivo al valor, no solo al evento input del DOM.
  // El texto dictado entra programáticamente (setInput(transcript)) y sin este
  // efecto el textarea quedaba clipeado en la altura mínima. El helper ajusta
  // a líneas completas (min 2 / max 8): ninguna línea cortada al medio.
  useLayoutEffect(() => {
    if (textareaRef.current) {
      resizeTextareaToContent(textareaRef.current);
    }
  }, [input]);

  // Silenciar audio actual cuando el usuario empieza a grabar (sin desactivar autoplay)
  useEffect(() => {
    if (recording && onCancelSpeech) {
      onCancelSpeech(); // Solo silencia el audio actual, mantiene autoplay activo
    }
  }, [recording]); // eslint-disable-line react-hooks/exhaustive-deps

  // Push-to-talk: mantener Alt para grabar (como el botón de micrófono)
  useEffect(() => {
    if (!micSupported) return;

    let recordTimeout: ReturnType<typeof setTimeout> | null = null;
    let comboDetected = false;

    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Alt') {
        if (!recording && !disabled && !micDisabled && !recordTimeout) {
          comboDetected = false;
          // Delay chico para no disparar con combos (Alt+Tab, Alt+F4, etc.)
          recordTimeout = setTimeout(() => {
            if (!comboDetected) {
              if (voiceNoteMode) void recorder.start();
              // Con el texto ACTUAL del input como base: dictar con Alt tras
              // editar a mano rebasaba sobre un transcript viejo y pisaba la
              // edición (mismo síntoma que el borrado).
              else startListening(inputRef.current);
            }
            recordTimeout = null;
          }, 120);
        }
      } else if (e.altKey) {
        // Otra tecla + Alt = combo, cancelar grabación
        comboDetected = true;
        if (recordTimeout) { clearTimeout(recordTimeout); recordTimeout = null; }
      }
    };

    const onKeyUp = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Alt') {
        if (recordTimeout) { clearTimeout(recordTimeout); recordTimeout = null; }
        if (voiceNoteMode) recorder.stop();
        else if (isListening) stopListening();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      if (recordTimeout) clearTimeout(recordTimeout);
    };
  }, [isListening, recording, disabled, micDisabled, micSupported, voiceNoteMode, startListening, stopListening, recorder.start, recorder.stop]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = () => {
    const trimmed = input.trim();
    if (trimmed && !disabled) {
      onSend(trimmed);
      setInput('');
      resetTranscript();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Planillas: chat → importador inteligente (2026-11-01) ──────────────────
  // El archivo NO pasa por el modelo (no lee adjuntos): si es .xlsx/.csv se
  // guarda en el handoff y se abre el ImportAnimalsModal de Mi Ganado con el
  // archivo ya cargado (detecta columnas solo, preview editable).
  const handleSpreadsheet = (file: File) => {
    setFileNotice(null);
    if (isSpreadsheetFile(file)) {
      // sessionId de origen: al terminar la importación, la web avisa al bot
      // (POST /api/chat/import/notify) para que el agente SEPA qué se cargó.
      setHandoffFile(file, getActiveSessionId() || undefined);
      navigate('/animals?open_import=1&from=chat');
    } else {
      setFileNotice('El chat no lee archivos. Si es una planilla de animales (.xlsx/.csv), adjuntala acá y la abre el importador.');
    }
  };
  const handleAttachClick = () => fileInputRef.current?.click();
  const handleFileChange = (e: { target: { files: FileList | null; value: string } }) => {
    const file = e.target.files?.[0];
    if (file) handleSpreadsheet(file);
    e.target.value = ''; // permite volver a elegir el mismo archivo
  };
  const handleDrop = (e: ReactDragEvent<HTMLTextAreaElement>) => {
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      e.preventDefault();
      handleSpreadsheet(file);
    }
  };

  // Push-to-talk: mantener apretado el botón para hablar, soltar para parar.
  // Tap sobre el mic mientras escucha = frenar (KAN-225: si el browser no
  // corta solo, el usuario siempre tiene vía de escape manual).
  const handleMicPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    // Sólo botón primario (mouse izquierdo / touch). Ignora clicks derechos/medios.
    if (e.button !== 0) return;
    console.log('[Dictado] mic pointerDown', { isListening, disabled, micDisabled });
    if (disabled || micDisabled) return;
    if (voiceNoteMode) {
      if (recorder.isRecording) {
        recorder.stop();
        return;
      }
      e.preventDefault();
      void recorder.start();
      return;
    }
    if (isListening) {
      stopListening();
      return;
    }
    // Evitar robar foco del textarea y selección de texto durante la pulsación.
    e.preventDefault();
    // Pasar el texto actual para continuar dictando sobre él.
    startListening(input);
  };

  const handleMicStop = () => {
    if (voiceNoteMode) {
      recorder.stop();
      return;
    }
    if (isListening) stopListening();
  };

  // Limpiar error cuando cambia el estado de listening
  useEffect(() => {
    if (isListening && speechError) {
      // Error se limpia automáticamente al empezar a escuchar
    }
  }, [isListening, speechError]);

  return (
    <div className="border-t border-hairline bg-paper p-2">
      <div className="flex items-end space-x-3">
        {/* Planilla al chat → importador (input oculto del 📎) */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          onChange={handleFileChange}
          aria-hidden="true"
          tabIndex={-1}
        />

        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            data-ar-guide="chat-input"
            value={input}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            className={`w-full px-2 py-2 pr-12 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest disabled:bg-paper-2 disabled:cursor-not-allowed transition-colors ${
              dragOver ? 'border-forest bg-forest/5' : 'border-hairline bg-white text-ink placeholder:text-sage/70'
            }`}
            // KAN-183: la altura la maneja resizeTextareaToContent (snap a
            // líneas completas) — sin topes en px que corten media línea.
            onInput={(e) => resizeTextareaToContent(e.target as HTMLTextAreaElement)}
          />

          {/* Botón 📎: adjuntar planilla → abre el importador de animales */}
          <button
            type="button"
            onClick={handleAttachClick}
            disabled={disabled}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-sage/70 hover:text-forest hover:bg-forest/10 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Adjuntar planilla de animales (abre el importador)"
            title="Adjuntar planilla (.xlsx/.csv): abre el importador de animales"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
          </button>

          {dragOver && (
            <div className="absolute bottom-full left-0 right-0 mb-1 px-3 py-2 bg-forest/10 border border-forest/30 rounded-lg text-sm text-forest">
              Soltá la planilla acá — abre el importador de animales 📊
            </div>
          )}
          {fileNotice && !dragOver && (
            <div className="absolute bottom-full left-0 right-0 mb-1 px-3 py-2 bg-warning/10 border border-warning/30 rounded-lg text-sm text-ink">
              {fileNotice}
            </div>
          )}

          {/* Nota de voz offline (KAN-225): error, grabación con contador o aviso de tope */}
          {voiceNoteMode && recorder.error && (
            <div className="absolute bottom-full left-0 right-0 mb-1 px-3 py-2 bg-danger/10 border border-danger/30 rounded-lg text-sm text-danger">
              <span className="flex items-center gap-1">
                <span>⚠️</span>
                <span>{recorder.error}</span>
              </span>
            </div>
          )}
          {voiceNoteMode && !recorder.error && recorder.isRecording && (
            <div className="absolute bottom-full left-0 right-0 mb-1 px-3 py-2 bg-danger/10 border border-danger/30 rounded-lg text-sm text-danger">
              <span className="flex items-center gap-2">
                <span className="inline-flex h-2 w-2 rounded-pixel bg-danger animate-pulse" />
                <span>Grabando nota de voz…</span>
                <span className="ml-auto tabular-nums">
                  {formatVoiceNoteDuration(recorder.elapsedMs)} / {formatVoiceNoteDuration(MAX_VOICE_NOTE_MS)}
                </span>
              </span>
            </div>
          )}
          {voiceNoteMode && !recorder.error && !recorder.isRecording && recorder.maxReached && (
            <div className="absolute bottom-full left-0 right-0 mb-1 px-3 py-2 bg-warning/10 border border-warning/30 rounded-lg text-sm text-ink">
              Llegaste al máximo de 3 minutos. Grabá otra nota para seguir.
            </div>
          )}

          {/* Mostrar error o transcripción intermedia */}
          {!voiceNoteMode && speechError && (
            <div className="absolute bottom-full left-0 right-0 mb-1 px-3 py-2 bg-danger/10 border border-danger/30 rounded-lg text-sm text-danger">
              <span className="flex items-center gap-1">
                <span>⚠️</span>
                <span>{speechError}</span>
              </span>
            </div>
          )}

          {!voiceNoteMode && isListening && !speechError && (
            <div className="absolute bottom-full left-0 right-0 mb-1 px-3 py-2 bg-success/10 border border-success/30 rounded-lg text-sm text-success">
              {interimTranscript ? (
                <span className="italic">"{interimTranscript}"</span>
              ) : (
                <span className="flex items-center gap-1">
                  <span className="animate-pulse">🎤</span>
                  <span>Escuchando...</span>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Botón de micrófono (push-to-talk: mantener apretado para hablar).
            Online dicta con la Web Speech API. Offline (KAN-225) graba una nota
            de voz que se transcribe al reconectar; para visitantes queda
            deshabilitado (micDisabled). */}
        {micSupported && (
          <button
            data-ar-guide="chat-mic"
            onPointerDown={handleMicPointerDown}
            onPointerUp={handleMicStop}
            onPointerLeave={handleMicStop}
            onPointerCancel={handleMicStop}
            onContextMenu={(e) => e.preventDefault()}
            disabled={disabled || micDisabled}
            style={{ touchAction: 'none' }}
            className={`p-3 rounded-md transition-all shadow-card hover:shadow-lift focus:outline-none focus:ring-2 focus:ring-offset-2 select-none ${
              micDisabled ? 'opacity-40 cursor-not-allowed ' : ''
            }${
              recording
                ? 'bg-danger hover:bg-danger/90 text-paper focus:ring-danger animate-pulse'
                : 'bg-paper-2 hover:bg-hairline/60 text-sage focus:ring-hairline'
            }`}
            aria-label={voiceNoteMode
              ? (recording ? 'Detener nota de voz' : 'Grabar nota de voz')
              : (isListening ? 'Detener grabación' : 'Grabar mensaje de voz')}
            title={micDisabled
              ? micDisabledHint
              : voiceNoteMode
                ? (recording ? 'Soltá para guardar la nota de voz' : 'Sin conexión: mantené apretado para grabar una nota de voz')
                : (isListening ? 'Detener grabación' : 'Mantener apretado para hablar')}
          >
            <MicrophoneIcon className="h-5 w-5" />
          </button>
        )}

        <button
          onClick={handleSend}
          disabled={disabled || !input.trim()}
          className="p-3 bg-forest text-paper rounded-md hover:bg-forest-mid focus:outline-none focus:ring-2 focus:ring-forest/30 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-card hover:shadow-lift"
          aria-label="Enviar mensaje"
        >
          <SendIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="mt-2 text-xs text-sage text-center flex justify-between items-center font-medium">
        {isMobile ? (
          <span>Mantener el micrófono para hablar</span>
        ) : (
          <span>Enter para enviar · Shift+Enter nueva línea · <strong>Alt</strong> para hablar · 📎 planilla de animales</span>
        )}
        {micSupported && (
          <span className={`text-sm ${recording ? (voiceNoteMode ? 'text-danger' : 'text-success') + ' animate-pulse' : 'text-forest'}`}>
            {voiceNoteMode
              ? (recording ? '🎤 Grabando…' : '🎤 Nota de voz')
              : (isListening ? '🎤 Escuchando...' : '🎤 Micrófono')}
          </span>
        )}
      </div>
    </div>
  );
};

export default ChatInput;
